import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { LEGAL, LEGAL_ORDER, ENTITY, JURISDICTION, EFFECTIVE_DATE, LAST_UPDATED, CONTACT_EMAIL } from "../legal/content";

export default function Legal() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const doc = LEGAL[slug];
  const containerRef = useRef(null);
  const [active, setActive] = useState(doc?.sections?.[0]?.id);

  useEffect(() => {
    if (!doc) return;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [doc, hash, slug]);

  // Track active section via IntersectionObserver for sticky TOC
  useEffect(() => {
    if (!doc) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.target.offsetTop - b.target.offsetTop);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    doc.sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [doc]);

  if (!doc) return <Navigate to="/legal/privacy" replace />;

  return (
    <div data-testid={`legal-${slug}`} className="bg-ink">
      {/* Masthead */}
      <header className="border-b border-white/15">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] font-mono text-ash uppercase mb-6">
            <Link to="/" className="hover:text-blood">TOXIC/STREET</Link>
            <span>/</span>
            <span className="text-blood">LEGAL</span>
          </div>
          <h1 className="font-anton uppercase text-bone text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85]">
            {doc.title}
          </h1>
          <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-3xl border-t border-white/15 pt-6 text-[11px] tracking-[0.3em] uppercase font-mono text-ash">
            <div>
              <div className="text-blood">// EFFECTIVE</div>
              <div className="text-bone mt-1">{EFFECTIVE_DATE}</div>
            </div>
            <div>
              <div className="text-blood">// LAST REVIEW</div>
              <div className="text-bone mt-1">{LAST_UPDATED}</div>
            </div>
            <div>
              <div className="text-blood">// JURISDICTION</div>
              <div className="text-bone mt-1">{JURISDICTION}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* TOC sidebar */}
        <aside className="lg:col-span-3 lg:sticky lg:top-32 self-start">
          <div className="text-[10px] tracking-[0.4em] uppercase font-mono text-blood mb-4">// CONTENTS</div>
          <nav className="border-l border-white/15 pl-4 space-y-2">
            {doc.sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`toc-${s.id}`}
                className={`block text-[12px] tracking-[0.15em] uppercase font-mono transition-colors leading-snug ${
                  active === s.id ? "text-blood" : "text-bone hover:text-blood"
                }`}
              >
                <span className="text-ash mr-2">§{s.n}</span>{s.h}
              </a>
            ))}
          </nav>

          <div className="mt-10 text-[10px] tracking-[0.4em] uppercase font-mono text-blood mb-4">// OTHER POLICIES</div>
          <ul className="border-l border-white/15 pl-4 space-y-2">
            {LEGAL_ORDER.filter((o) => o.slug !== slug).map((o) => (
              <li key={o.slug}>
                <Link
                  to={`/legal/${o.slug}`}
                  data-testid={`legal-link-${o.slug}`}
                  className="text-[12px] tracking-[0.15em] uppercase font-mono text-bone hover:text-blood"
                >
                  {o.label}
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main column */}
        <article className="lg:col-span-9" ref={containerRef}>
          <p className="text-bone text-base leading-relaxed font-mono border-l-2 border-blood pl-6 italic">
            {doc.summary}
          </p>

          <div className="mt-12 space-y-14">
            {doc.sections.map((s) => (
              <section key={s.id} id={s.id} data-testid={`section-${s.id}`} className="scroll-mt-32">
                <div className="flex items-baseline gap-4 border-b border-white/10 pb-3 mb-5">
                  <span className="font-anton text-blood text-3xl tracking-tighter">§{s.n}</span>
                  <h2 className="font-anton uppercase text-bone text-2xl sm:text-3xl tracking-tight leading-tight">
                    {s.h}
                  </h2>
                </div>
                <div className="space-y-4 text-bone text-[15px] leading-[1.7] font-mono">
                  {s.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Sign-off */}
          <div className="mt-16 pt-8 border-t border-white/15 text-[11px] tracking-[0.3em] uppercase font-mono text-ash space-y-1">
            <div><span className="text-blood">©</span> {new Date().getFullYear()} {ENTITY}. ALL RIGHTS RESERVED.</div>
            <div>Inquiries: <a href={`mailto:${CONTACT_EMAIL}`} className="text-bone hover:text-blood underline-offset-4 hover:underline">{CONTACT_EMAIL}</a></div>
          </div>
        </article>
      </div>
    </div>
  );
}
